import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { contactsAPI, formatApiErrorDetail } from '../utils/api';
import getCanonicalUrl from '../utils/canonical';

const Contatti = () => {
  const [formData, setFormData] = useState({
    nome: '',
    eta: '',
    telefono: '',
    citta: '',
    sede: '',
    messaggio: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSedeChange = (value) => {
    setFormData(prev => ({ ...prev, sede: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nome || !formData.telefono || !formData.sede) {
      toast.error('Per favore, compila i campi obbligatori');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        ...formData,
        eta: formData.eta ? parseInt(formData.eta) : null
      };
      await contactsAPI.create(submitData);
      toast.success('Richiesta inviata con successo! Vi contatteremo presto.');
      setFormData({
        nome: '',
        eta: '',
        telefono: '',
        citta: '',
        sede: '',
        messaggio: ''
      });
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail);
      toast.error(errorMsg || 'Errore durante l\'invio. Riprova.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <Helmet>
        <title>Scrivici | Agenzia Matrimoniale in Campania | Officine del Cuore</title>
        <meta name="description" content="Contatta adesso Officine del Cuore agenzia matrimoniale e di incontri selezionati Officine del Cuore. Inizia oggi la ricerca della tua anima gemella" />
        <link rel="canonical" href={getCanonicalUrl('contatti')} />
      </Helmet>

      {/* Hero Section */}
      <section className="page-hero contatti-hero" style={{backgroundImage: 'url(https://images.unsplash.com/photo-1759435578-231f6137aac5)'}}>
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="page-title fade-in">Scrivici o Chiamaci</h1>
          <p className="page-subtitle fade-in-delay">
            Siamo qui per ascoltarvi. Contattateci senza impegno.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-elegant">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="section-title mb-6">
                Richiedi un Colloquio Riservato
              </h2>
              <p className="contact-intro">
                Compila il modulo e ti contatteremo entro 24 ore per fissare 
                un colloquio telefonico o in videochiamata, completamente gratuito e senza impegno.
              </p>
              
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <Label htmlFor="nome">Nome e Cognome *</Label>
                  <Input
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder="Mario Rossi"
                    required
                    className="input-elegant"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="eta">Età</Label>
                  <Input
                    id="eta"
                    name="eta"
                    type="number"
                    value={formData.eta}
                    onChange={handleChange}
                    placeholder="35"
                    className="input-elegant"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="+39 333 123 4567"
                    required
                    className="input-elegant"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="citta">Città</Label>
                  <Input
                    id="citta"
                    name="citta"
                    value={formData.citta}
                    onChange={handleChange}
                    placeholder="Napoli"
                    className="input-elegant"
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="sede">Sede di Riferimento *</Label>
                  <Select value={formData.sede} onValueChange={handleSedeChange}>
                    <SelectTrigger className="input-elegant">
                      <SelectValue placeholder="Seleziona una sede" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="napoli">Napoli</SelectItem>
                      <SelectItem value="caserta">Caserta</SelectItem>
                      <SelectItem value="roma">Roma</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="messaggio">Messaggio</Label>
                  <Textarea
                    id="messaggio"
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleChange}
                    placeholder="Raccontaci brevemente cosa stai cercando..."
                    rows={5}
                    className="input-elegant"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
                  <Send className="ml-2" size={18} />
                </Button>

                <p className="privacy-note-small">
                  I tuoi dati saranno trattati con massima riservatezza e non saranno mai condivisi con terzi.
                </p>
                
                <p className="text-xs text-burgundy/70 mt-3 italic">
                  (*) Inviando il modulo accetto l'informativa sulla privacy policy. Sono al corrente che i miei dati saranno trattati nel rispetto della Privacy, come da Regolamento europeo 679/2016-GDPR.
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div>
              <h2 className="section-title mb-6">
                Le Nostre Sedi
              </h2>
              
              <div className="space-y-6">
                {/* Napoli */}
                <Card className="contact-card">
                  <CardContent className="p-6">
                    <h3 className="contact-card-title">Napoli</h3>
                    <div className="contact-info-list">
                      <div className="contact-info-item">
                        <MapPin className="text-gold" size={20} />
                        <div>
                          <p className="font-medium">Via G. Testa, 16</p>
                          <p className="text-sm">Napoli (NA)</p>
                        </div>
                      </div>
                      <div className="contact-info-item">
                        <Phone className="text-gold" size={20} />
                        <a href="tel:+393292626152" className="hover:text-gold transition-colors">
                          +39 3292626152
                        </a>
                      </div>
                      <div className="contact-info-item">
                        <Mail className="text-gold" size={20} />
                        <a href="mailto:info@officinedelcuore.it" className="hover:text-gold transition-colors">
                          info@officinedelcuore.it
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Caserta */}
                <Card className="contact-card">
                  <CardContent className="p-6">
                    <h3 className="contact-card-title">Caserta</h3>
                    <div className="contact-info-list">
                      <div className="contact-info-item">
                        <MapPin className="text-gold" size={20} />
                        <div>
                          <p className="font-medium">Viale Vincenzo Cappiello, 8</p>
                          <p className="text-sm">Caserta (CE)</p>
                        </div>
                      </div>
                      <div className="contact-info-item">
                        <Phone className="text-gold" size={20} />
                        <a href="tel:+393520567001" className="hover:text-gold transition-colors">
                          +39 3520567001
                        </a>
                      </div>
                      <div className="contact-info-item">
                        <Mail className="text-gold" size={20} />
                        <a href="mailto:info@officinedelcuore.it" className="hover:text-gold transition-colors">
                          info@officinedelcuore.it
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Roma */}
                <Card className="contact-card">
                  <CardContent className="p-6">
                    <h3 className="contact-card-title">Roma</h3>
                    <div className="contact-info-list">
                      <div className="contact-info-item">
                        <MapPin className="text-gold" size={20} />
                        <div>
                          <p className="font-medium">Via Mattia Battistini, 466a</p>
                          <p className="text-sm">Roma</p>
                        </div>
                      </div>
                      <div className="contact-info-item">
                        <Phone className="text-gold" size={20} />
                        <a href="tel:+393455006556" className="hover:text-gold transition-colors">
                          Telefono/WhatsApp: +39 3455006556
                        </a>
                      </div>
                      <div className="contact-info-item">
                        <Mail className="text-gold" size={20} />
                        <a href="mailto:info@officinedelcuore.it" className="hover:text-gold transition-colors">
                          info@officinedelcuore.it
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* General Info */}
                <Card className="contact-card highlight">
                  <CardContent className="p-6">
                    <h3 className="contact-card-title">Orari di Apertura</h3>
                    <div className="text-sm text-burgundy/80 space-y-1">
                      <p>Lunedì - Venerdì: 10:00 - 19:00</p>
                      <p>Sabato: 10:00 - 18:00</p>
                      <p>Domenica: Chiuso</p>
                      <p className="mt-3 font-medium text-burgundy">
                        Colloqui anche su appuntamento in orari serali
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-elegant bg-warm-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="section-title mb-6">
            La Vostra Privacy è Sacra
          </h2>
          <p className="section-description">
            Ogni informazione che condividete con noi viene trattata con la massima riservatezza. 
            Non condivideremo mai i vostri dati con terzi e rispetteremo sempre la vostra privacy. 
            I colloqui avvengono in ambienti riservati o da remoto, secondo la vostra preferenza.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
